// src/features/simulators/shared/hooks/useHotelDevices.ts

import { useState, useEffect } from 'react';
import { useDataProvider } from 'react-admin';

export interface DeviceOption {
  id: string;
  installationCode: string;  // Backend usa "installationCode"
  deviceType: string;         // Backend usa "deviceType" (no "type")
  channel: string;            // Backend usa "channel"
  status: string;             // Backend usa "status"
  hotelId: string;
  groupId?: string;
  source: string;
  registeredAt: string;
  lastSeenAt?: string;
}

interface UseHotelDevicesReturn {
  devices: DeviceOption[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseHotelDevicesFilters {
  deviceType?: string;
  channel?: string;
}

/**
 * Hook to fetch devices for a specific hotel with optional filters
 * Uses React Admin's dataProvider for consistency
 * 
 * @param hotelId - Hotel ID to filter devices
 * @param filters - Optional filters for deviceType and channel
 * @example
 * // Get only Kiosk devices
 * useHotelDevices('hotel-123', { deviceType: 'Kiosk', channel: 'Kiosk' })
 */
export const useHotelDevices = (
  hotelId: string | null,
  filters?: UseHotelDevicesFilters
): UseHotelDevicesReturn => {
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider();

  const fetchDevices = async () => {
    if (!hotelId) {
      setDevices([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build filter object
      const filterObject: Record<string, string> = { hotelId };
      
      // Add optional filters
      if (filters?.deviceType) {
        filterObject.deviceType = filters.deviceType;
      }
      if (filters?.channel) {
        filterObject.channel = filters.channel;
      }

      // Use dataProvider.getList with filters
      const result = await dataProvider.getList('devices', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'installationCode', order: 'ASC' },
        filter: filterObject,
      });

      setDevices(result.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [hotelId, filters?.deviceType, filters?.channel]);

  return {
    devices,
    isLoading,
    error,
    refetch: fetchDevices,
  };
};